import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Bounce, toast } from "react-toastify";
import { ComplaintsMapValue, Perspectives } from "@shared/types";
import { useTranslation } from "react-i18next";

export type ControlledUseComplaintsPointsProps = {
  value: ComplaintsMapValue;
  onChange: (value: ComplaintsMapValue) => void;
};

export type UncontrolledUseComplaintsPointsProps = {
  value?: undefined;
  onChange?: undefined;
};

export const useComplaintsPoints = ({
  value,
  onChange,
}:
  | ControlledUseComplaintsPointsProps
  | UncontrolledUseComplaintsPointsProps) => {
  const [perspective, setPerspective] = useState<Perspectives>("front");
  const { t } = useTranslation();

  const [complaintsPoints, setComplaintsPoints] = useState<ComplaintsMapValue>({
    front: [],
    back: [],
    left: [],
    right: [],
  });

  const [wasDeleteHintAlreadyShown, setWasDeleteHintAlreadyShown] =
    useState(false);

  const handleAddComplaintsPoint =
    (perspective: "front" | "back" | "left" | "right") =>
    (cx: number, cy: number) => {
      const newPoint = { id: uuidv4(), cx, cy };

      if (onChange) {
        onChange({
          ...value,
          [perspective]: [...value[perspective], newPoint],
        });
      } else {
        setComplaintsPoints((prev) => {
          return { ...prev, [perspective]: [...prev[perspective], newPoint] };
        });
      }
    };

  const removeComplaintsPoint = (perspective: Perspectives, id: string) => {
    if (onChange) {
      onChange({
        ...value,
        [perspective]: value[perspective].filter((point) => point.id !== id),
      });
    } else {
      setComplaintsPoints((prev) => {
        return {
          ...prev,
          [perspective]: prev[perspective].filter((point) => point.id !== id),
        };
      });
    }
  };

  const handleRemoveComplaintsPointClick =
    (perspective: "front" | "back" | "left" | "right") => (id: string) => {
      if (!wasDeleteHintAlreadyShown) {
        setWasDeleteHintAlreadyShown(true);
        toast(t("pages.complaints_map.remove_complaints_area_toast"), {
          position: "top-center",
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }

      removeComplaintsPoint(perspective, id);
    };

  return {
    complaintsPoints: value || complaintsPoints,
    handleAddComplaintsPoint,
    handleRemoveComplaintsPointClick,
    perspective,
    setPerspective,
  };
};
